import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { data as formatarData, numeroDeProcesso } from '../util/formato';
import { Alerta, Botao, Ficha } from './base';
import { Campo } from './campos';

export interface ProcessoDoCaso {
  readonly id: string;
  readonly numeroProcesso: string | null;
  readonly vara: string | null;
  readonly comarca: string | null;
  readonly status: string;
  readonly proximoPrazo: string | null;
}

/**
 * Processo judicial do caso — na prática, o número.
 *
 * A tabela guarda vara, comarca, valor da causa e próximo prazo desde o começo, e
 * a API sempre soube gravá-los; faltava a tela. O que a usuária pede todo dia é
 * só o NÚMERO: sem ele, o processo em curso vivia nas observações, onde nada
 * consegue procurar por ele depois.
 *
 * O que já estiver preenchido por outro caminho (vara, comarca, prazo) aparece
 * aqui ao lado do número — mas quem edita isto não é esta tela.
 *
 * Cadastrar um processo move o caso para a fase judicial: é o estado dele agora,
 * e é o servidor que faz isso, não esta tela.
 */
export function ProcessoDoCaso({
  devedorId,
  processos,
}: {
  devedorId: string;
  processos: readonly ProcessoDoCaso[];
}) {
  const clienteConsulta = useQueryClient();
  const [numero, setNumero] = useState('');
  const [editando, setEditando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  function recarregar() {
    void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
    void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
  }

  function aoFalhar(e: unknown) {
    setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível salvar o número.');
  }

  const registrar = useMutation({
    mutationFn: () => api.criar(`/devedores/${devedorId}/processos`, { numeroProcesso: numero.trim() }),
    onSuccess: () => {
      setNumero('');
      setErro(null);
      recarregar();
    },
    onError: aoFalhar,
  });

  const alterar = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: string }) =>
      api.alterar(`/processos/${id}`, { numeroProcesso: valor.trim() }),
    onSuccess: () => {
      setEditando(null);
      setNumero('');
      setErro(null);
      recarregar();
    },
    onError: aoFalhar,
  });

  const remover = useMutation({
    mutationFn: (id: string) => api.remover(`/processos/${id}`),
    onSuccess: () => {
      setErro(null);
      recarregar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível remover o processo.'),
  });

  const salvando = registrar.isPending || alterar.isPending;

  return (
    <>
      <div className="flex items-center justify-between gap-3 p-5 pb-3">
        <h2 className="text-lg text-[color:var(--texto)]">
          Processo judicial
          {processos.length > 0 && (
            <span className="ml-3 text-xs font-normal text-[color:var(--texto-tenue)]">
              {processos.length}
            </span>
          )}
        </h2>
      </div>

      {erro && (
        <div className="px-5 pb-3">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      {processos.length > 0 && (
        <ul className="border-t">
          {processos.map((p) => (
            <li key={p.id} className="border-b px-5 py-3 text-sm last:border-0">
              {editando === p.id ? (
                <div className="space-y-2">
                  <Campo
                    rotulo="Número do processo"
                    valor={numero}
                    aoMudar={setNumero}
                    placeholder="0000000-00.0000.0.00.0000"
                  />
                  <div className="flex gap-2">
                    <Botao
                      tipo="primario"
                      onClick={() => alterar.mutate({ id: p.id, valor: numero })}
                      desabilitado={salvando || numero.trim().length === 0}
                      className="px-2.5 py-1 text-xs"
                    >
                      Salvar
                    </Botao>
                    <Botao
                      tipo="discreto"
                      onClick={() => {
                        setEditando(null);
                        setNumero('');
                      }}
                      className="px-0 text-xs"
                    >
                      Cancelar
                    </Botao>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <p className="tabular font-medium text-[color:var(--texto)]">
                      {numeroDeProcesso(p.numeroProcesso)}
                    </p>
                    <Ficha tom={p.status === 'ENCERRADO' ? 'neutro' : 'acento'}>
                      {p.status.toLowerCase().replace(/_/g, ' ')}
                    </Ficha>
                  </div>
                  {(p.vara || p.comarca || p.proximoPrazo) && (
                    <p className="mt-0.5 text-xs text-[color:var(--texto-tenue)]">
                      {[
                        p.vara,
                        p.comarca && `comarca de ${p.comarca}`,
                        p.proximoPrazo && `próximo prazo ${formatarData(p.proximoPrazo)}`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Botao
                      onClick={() => {
                        setEditando(p.id);
                        setNumero(p.numeroProcesso ?? '');
                        setErro(null);
                      }}
                      className="px-2.5 py-1 text-xs"
                    >
                      Alterar número
                    </Botao>
                    <Botao
                      tipo="discreto"
                      onClick={() => {
                        if (window.confirm('Remover este processo do caso?')) remover.mutate(p.id);
                      }}
                      desabilitado={remover.isPending}
                      className="px-0 text-xs"
                    >
                      Remover
                    </Botao>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {editando === null && (
        <div className="space-y-2 border-t px-5 py-4">
          <Campo
            rotulo={processos.length === 0 ? 'Número do processo' : 'Outro processo'}
            valor={numero}
            aoMudar={setNumero}
            placeholder="0000000-00.0000.0.00.0000"
            {...(processos.length === 0
              ? {
                  auxilio:
                    'Só quando a ação já foi distribuída. Registrar aqui move o caso para a fase judicial.',
                }
              : {})}
          />
          <div className="flex justify-end">
            <Botao
              tipo="primario"
              onClick={() => registrar.mutate()}
              desabilitado={salvando || numero.trim().length === 0}
              className="px-3 py-1.5 text-xs"
            >
              {registrar.isPending ? 'Salvando…' : 'Salvar'}
            </Botao>
          </div>
        </div>
      )}
    </>
  );
}
